import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { rupee, effectivePrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { ShoppingBasket, Plus, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export function FrequentlyBoughtTogether({
  productId,
  cartProductIds,
  title = "Frequently bought together",
  subtitle = "Complete your basket with these picks",
  apiPath = "/products/recommendations",
  className = "",
}) {
  const { addItem } = useCart();
  const [items, setItems] = useState([]);
  const [added, setAdded] = useState({});

  const cartKey = (cartProductIds || []).join(",");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let res;
        if (productId) {
          res = await api.get(`/products/${productId}/frequently-bought-together`, { params: { limit: 8 } });
        } else if (cartProductIds && cartProductIds.length) {
          res = await api.post(apiPath, { product_ids: cartProductIds, limit: 8 });
        } else {
          if (active) setItems([]);
          return;
        }
        if (active) setItems(res.data || []);
      } catch {
        if (active) setItems([]);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, cartKey, apiPath]);

  if (!items.length) return null;

  const onAdd = (p) => {
    if (p.stock <= 0) return;
    addItem(p, 1);
    setAdded((s) => ({ ...s, [p.id]: true }));
    toast.success(`${p.name} added to cart`);
    setTimeout(() => setAdded((s) => ({ ...s, [p.id]: false })), 1400);
  };

  return (
    <section
      className={`rounded-2xl border border-border/60 bg-white p-5 shadow-ambient ${className}`}
      data-testid="fbt-section"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/20 text-ink">
          <Sparkles className="h-4 w-4" />
        </span>
        <div>
          <h2 className="font-heading text-xl text-ink" data-testid="fbt-title">{title}</h2>
          <p className="text-xs text-ink-muted">{subtitle}</p>
        </div>
      </div>

      <div className="no-scrollbar -mx-1 mt-4 flex gap-3 overflow-x-auto px-1 pb-1">
        {items.map((p) => {
          const img = fileUrl(p.image_id);
          const soldOut = p.stock <= 0;
          const hasDiscount = p.discount_price && p.discount_price > 0;
          const isAdded = added[p.id];
          return (
            <div
              key={p.id}
              className="flex w-[148px] shrink-0 flex-col overflow-hidden rounded-xl border border-border/60 bg-cream-alt/40"
              data-testid={`fbt-item-${p.id}`}
            >
              <Link to={`/products/${p.id}`} className="block">
                <div className="aspect-square w-full overflow-hidden bg-cream-alt">
                  {img ? (
                    <img src={img} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-ink-muted">
                      <ShoppingBasket className="h-8 w-8" />
                    </div>
                  )}
                </div>
              </Link>
              <div className="flex flex-1 flex-col p-2.5">
                <Link to={`/products/${p.id}`}>
                  <h3 className="text-sm font-semibold leading-snug text-ink line-clamp-2 hover:text-brand">{p.name}</h3>
                </Link>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-sm font-bold text-ink">{rupee(effectivePrice(p))}</span>
                  {hasDiscount && <span className="text-xs text-ink-muted line-through">{rupee(p.price)}</span>}
                </div>
                <button
                  onClick={() => onAdd(p)}
                  disabled={soldOut}
                  data-testid={`fbt-add-${p.id}`}
                  className={`mt-2 inline-flex items-center justify-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    soldOut
                      ? "cursor-not-allowed border border-border text-ink-muted"
                      : isAdded
                        ? "bg-sage text-white"
                        : "bg-brand text-ink hover:bg-brand-hover"
                  }`}
                >
                  {soldOut ? "Out of stock" : isAdded ? (<><Check className="h-3.5 w-3.5" /> Added</>) : (<><Plus className="h-3.5 w-3.5" /> Add</>)}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
